/**
 * ContactsTab — F11 T11.3 FE (refactor F12.M Etap 1.6, 2026-05-13).
 *
 * Zakladka „Kontakty" w karcie kontrahenta. Listing kontaktow + akcje per row.
 *
 * **Etap 1.6 refactor:** po wprowadzeniu Kontaktów jako pełnoprawnego 5. modułu
 * (`/contractor-contacts`, decyzja koordynatora 2026-05-13), ta zakładka jest
 * teraz **filtrowaną listą-skrót** prowadzącą do globalnego modułu:
 *  - Klik wierszem → `/contractor-contacts/:id` (fullscreen detail)
 *  - „Dodaj kontakt" → `/contractor-contacts/new?contractorId=:cId` (prefill)
 *  - „Edytuj" w row actions → `/contractor-contacts/:id`
 *  - „Ustaw głównym" / „Usuń" — pozostają inline (szybka akcja, bez navigate)
 *  - Link „Wszystkie kontakty →" → `/contractor-contacts?contractorId=:cId`
 *
 * Backend bez zmian (nadal `/api/contractors/:id/contacts` dla listy +
 * `PATCH/DELETE` per `:contactId`). Typ `Contact` żyje w `contact-types.ts`.
 *
 * UI patterns reuse:
 *   - Table z F8/F10 (sortable, paginate)
 *   - Kebab menu wzor z Users.tsx
 *   - ConfirmDialog z F9 (variant=danger dla delete)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Users } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  ConfigurableTable,
  type RowActionItem,
  RowActionsMenu,
} from '@/components/configurable-table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { useReportRecordLock } from '@/hooks/useReportRecordLock';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import { applyOrder } from '@/lib/kartoteki/formatka-order';

import { type Contact } from './contact-types';

interface ContactCategory {
  id: string;
  name: string;
}

/**
 * F12 PR 2 pkt 2 (ADR-0032): opcjonalny LayoutElement z buildera macierzystego
 * (`type='related_records_table'` w `tab.extras`). Zawiera `settings.displayFields`
 * (slugs pól modułu wskazywanego do pokazania w kolumnach) + `allowAdd/Edit/Delete`
 * (kontrola wiersza). Gdy undefined — zachowanie hardcoded (back-compat, 6 default columns).
 */
interface SubcollectionLayoutElement {
  id: string;
  type: string;
  settings?: {
    collection?: string;
    displayFields?: string[];
    allowAdd?: boolean;
    allowEdit?: boolean;
    allowDelete?: boolean;
    [key: string]: unknown;
  };
}

interface ContactsTabProps {
  contractorId: string;
  /**
   * F12 PR 2 pkt 2: layoutElement subcollection z buildera macierzystego.
   * Gdy podany — column filtering po `displayFields` + allowAdd/Edit/Delete toggle.
   * Gdy undefined — wszystkie 6 hardcoded kolumn + wszystkie akcje (back-compat).
   */
  layoutElement?: SubcollectionLayoutElement;
}

/**
 * F12 PR 2 pkt 2 — mapping slug pola modułu `contractor_contacts` na key kolumny
 * w table. Niektóre kolumny ('name', 'phone') reprezentują wiele pól (`first_name`
 * + `last_name` → 'name'; `phone` lub `mobile` → 'phone'). Klucze NIE występujące
 * w mapie są ignorowane przez column filter.
 */
const CONTACT_SLUG_TO_COLUMN_KEY: Record<string, string> = {
  first_name: 'name',
  last_name: 'name',
  position: 'position',
  email: 'email',
  phone: 'phone',
  mobile: 'phone',
  category_id: 'category',
};

export function ContactsTab({ contractorId, layoutElement }: ContactsTabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);

  // „Ustaw głównym" i „Usuń" to zwykły zapis kontaktu — na czas zadania przycisku SQL na nim serwer
  // odpowiada 409 `RECORD_LOCKED_BY_RUN`; zamiast ogólnego błędu pokazujemy, które zadanie blokuje kontakt.
  const reportRecordLock = useReportRecordLock();

  const contactsQ = useQuery({
    queryKey: ['contractor-contacts', contractorId],
    queryFn: async () => {
      const res = await api.get<Contact[]>(`/contractors/${contractorId}/contacts`);
      return res.data;
    },
    staleTime: 0,
  });

  // Kategoria rekordu — NOWA oś `record_categories` (kartoteka_key='contractor_contacts',
  // migracja 0127; zastąpiła stary słownik contact_categories). Lista czytana raz, mapowana
  // id→nazwa po stronie frontu do kolumny „Kategoria" (parytet z dawnym lookupem).
  const categoriesQ = useQuery({
    queryKey: ['record-categories', 'contractor_contacts'],
    queryFn: async () => {
      const res = await api.get<{ categories: ContactCategory[] }>('/record-categories', {
        params: { kartoteka: 'contractor_contacts' },
      });
      return res.data.categories;
    },
    staleTime: 5 * 60 * 1000, // 5 min — lista rzadko sie zmienia
  });

  const setPrimaryMutation = useMutation({
    mutationFn: async (contactId: string) => {
      await api.patch(`/contractors/${contractorId}/contacts/${contactId}`, {
        isPrimary: true,
      });
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.contacts.toast.primarySet'),
      });
      void queryClient.invalidateQueries({ queryKey: ['contractor-contacts', contractorId] });
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      const code = extractErrorCode(err);
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.contacts.toast.primaryError'),
        description: code,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (contactId: string) => {
      await api.delete(`/contractors/${contractorId}/contacts/${contactId}`);
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.contacts.toast.deleted'),
      });
      void queryClient.invalidateQueries({ queryKey: ['contractor-contacts', contractorId] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      const code = extractErrorCode(err);
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.contacts.toast.deleteError'),
        description: code,
      });
    },
  });

  const categoryById = new Map((categoriesQ.data ?? []).map((c) => [c.id, c.name]));

  function fullName(c: Contact): string {
    const parts = [c.firstName, c.lastName].filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
    return c.email ?? c.phone ?? '—';
  }

  const categoryOptions = (categoriesQ.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  const columns: ColumnDefinition<Contact>[] = [
    {
      id: 'name',
      header: t('tenant.contractors.contacts.columns.name'),
      accessor: (row) => fullName(row),
      renderCell: (_value, row) => (
        <div className="flex items-center gap-2">
          <Link
            to={`/contractor-contacts/${row.id}`}
            className="font-medium text-text-primary hover:text-accent-700 hover:underline"
            data-testid={`contact-row-link-${row.id}`}
          >
            {fullName(row)}
          </Link>
          {row.isPrimary && (
            <Badge color="accent" data-testid={`contact-primary-${row.id}`}>
              {t('tenant.contractors.contacts.badges.primary')}
            </Badge>
          )}
          {row.isDecisionMaker && (
            <Badge color="blue">{t('tenant.contractors.contacts.badges.decisionMaker')}</Badge>
          )}
        </div>
      ),
      sortable: true,
      filterable: true,
      headerFilter: { category: 'text', fieldSlug: 'name', fieldType: 'varchar' },
      defaultWidth: 260,
    },
    {
      id: 'position',
      header: t('tenant.contractors.contacts.columns.position'),
      accessor: (row) => row.position,
      sortable: true,
      filterable: true,
      headerFilter: { category: 'text', fieldSlug: 'position', fieldType: 'varchar' },
    },
    {
      id: 'email',
      header: t('tenant.contractors.contacts.columns.email'),
      accessor: (row) => row.email,
      renderCell: (value) =>
        typeof value === 'string' && value ? (
          <a href={`mailto:${value}`} className="text-accent-600 hover:underline">
            {value}
          </a>
        ) : null,
      sortable: true,
      filterable: true,
      headerFilter: { category: 'text', fieldSlug: 'email', fieldType: 'email' },
    },
    {
      id: 'phone',
      header: t('tenant.contractors.contacts.columns.phone'),
      accessor: (row) => row.phone ?? row.mobile,
      sortable: true,
      filterable: true,
      headerFilter: { category: 'text', fieldSlug: 'phone', fieldType: 'varchar' },
    },
    {
      id: 'category',
      header: t('tenant.contractors.contacts.columns.category'),
      accessor: (row) => row.categoryId,
      renderCell: (value) => (typeof value === 'string' ? (categoryById.get(value) ?? null) : null),
      sortable: true,
      headerFilter: {
        category: 'select',
        fieldSlug: 'categoryId',
        fieldType: 'select',
        options: categoryOptions,
      },
    },
  ];

  // F12 PR 2 pkt 2 (ADR-0032): filter columns na podstawie displayFields z buildera.
  // Gdy `layoutElement` undefined lub displayFields undefined → wszystkie 5 default columns
  // (back-compat); akcje (kebab) renderowane osobno przez `renderActions`, zawsze widoczne.
  const explicitDisplayFields = layoutElement?.settings?.displayFields;
  const allowAdd = layoutElement?.settings?.allowAdd ?? true;
  // allowEdit + allowDelete kontrolowane wewnątrz kebab per-item (TODO: F12 PR2 pkt2 sweep).
  const visibleIds = explicitDisplayFields
    ? new Set(
        explicitDisplayFields
          .map((slug) => CONTACT_SLUG_TO_COLUMN_KEY[slug])
          .filter((k): k is string => !!k),
      )
    : null;
  const visibleColumns = visibleIds ? columns.filter((c) => visibleIds.has(c.id)) : columns;
  // Kolejność kolumn z formatki bloku (drag) — `columnOrder` to lista `id` kolumn
  // (name/position/email/phone/category); nieujęte na końcu. Read-side == builder.
  const columnOrderRaw = layoutElement?.settings?.columnOrder;
  const columnOrder = Array.isArray(columnOrderRaw)
    ? columnOrderRaw.filter((s): s is string => typeof s === 'string')
    : [];
  const filteredColumns =
    columnOrder.length > 0
      ? applyOrder(
          visibleColumns.map((c) => c.id),
          columnOrder,
        )
          .map((id) => visibleColumns.find((c) => c.id === id))
          .filter((c): c is (typeof visibleColumns)[number] => !!c)
      : visibleColumns;

  function renderRowActions(row: Contact) {
    const items: RowActionItem[] = [
      {
        key: 'edit',
        label: t('tenant.contractors.contacts.actions.edit'),
        onSelect: () => navigate(`/contractor-contacts/${row.id}`),
        testId: `contact-action-edit-${row.id}`,
      },
      ...(row.isPrimary
        ? []
        : [
            {
              key: 'set-primary',
              label: t('tenant.contractors.contacts.actions.setPrimary'),
              onSelect: () => setPrimaryMutation.mutate(row.id),
              testId: `contact-action-set-primary-${row.id}`,
            },
          ]),
      {
        key: 'delete',
        label: t('tenant.contractors.contacts.actions.delete'),
        onSelect: () => setDeleteTarget(row),
        danger: true,
        testId: `contact-action-delete-${row.id}`,
      },
    ];
    return (
      <RowActionsMenu
        items={items}
        ariaLabel={t('tenant.contractors.contacts.actions.menuAria')}
        testId={`contact-actions-${row.id}`}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-text-primary">
            {t('tenant.contractors.contacts.title')}
          </h3>
          <p className="text-xs text-text-tertiary">{t('tenant.contractors.contacts.subtitle')}</p>
          <Link
            to={`/contractor-contacts?contractorId=${contractorId}`}
            className="mt-1 inline-flex items-center gap-1 text-xs text-accent-700 hover:underline"
            data-testid="contacts-tab-see-all-link"
          >
            {t('tenant.contractors.contacts.seeAllLink')}
            <ArrowRight size={12} aria-hidden="true" />
          </Link>
        </div>
        {allowAdd && (
          <Button
            onClick={() => navigate(`/contractor-contacts/new?contractorId=${contractorId}`)}
            data-testid="contacts-add-btn"
          >
            {t('tenant.contractors.contacts.addButton')}
          </Button>
        )}
      </div>

      {contactsQ.isError ? (
        <div
          role="alert"
          className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
        >
          <p>{t('tenant.contractors.contacts.loadError')}</p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-3"
            onClick={() => void contactsQ.refetch()}
          >
            {t('tenant.contractors.contacts.retry')}
          </Button>
        </div>
      ) : (
        <ConfigurableTable<Contact>
          tableId={`contractor-contacts-${contractorId}`}
          testIdPrefix="contacts"
          columns={filteredColumns}
          data={contactsQ.data ?? []}
          isLoading={contactsQ.isLoading}
          persistConfig={false}
          showViewModeToggle={false}
          compact
          stickyHeader={false}
          renderActions={renderRowActions}
          emptyIcon={Users}
          emptyTitle={t('tenant.contractors.contacts.empty')}
        />
      )}

      {/* F12.M Etap 1.6: add/edit modale wymienione na navigate do /contractor-contacts/...
          dawny ContactModal usunięty (typ Contact w contact-types.ts). */}

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('tenant.contractors.contacts.deleteConfirm.title')}
        message={
          deleteTarget
            ? t('tenant.contractors.contacts.deleteConfirm.message', {
                name: fullName(deleteTarget),
              })
            : ''
        }
        variant="danger"
        confirmLabel={t('common.delete')}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
